import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import axios from '../../api/client';
import EmptyState from '../EmptyState';
import { useCurrency } from '../../context/CurrencyContext';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import RepairApprovalActions, { ApprovalChip } from './RepairApprovalActions';
import RepairApprovalHistory from './RepairApprovalHistory';
import ScrollTable from '../ScrollTable';
import TableToolbar from '../TableToolbar';
import useTableSearch from '../../hooks/useTableSearch';
import TenantPicker from '../TenantPicker';

const RepairManagement = () => {
  const { formatMoney } = useCurrency();
  const [repairs, setRepairs] = useState([]);

  // Client-side: this screen loads every row, so filtering here searches all of
  // them. Screens that page on the server must search there instead.
  const {
    query, setQuery, filterValues, setFilter, reset,
    rows: visibleRepairs, resultCount, totalCount
  } = useTableSearch(repairs, {
    searchFields: ['title', 'description', 'tenant.name', 'property.propertyName'],
    filters: {
      status: (row, v) => row.status === v,
      approvalStatus: (row, v) => row.approvalStatus === v,
      priority: (row, v) => row.priority === v
    }
  });
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedTenantObj, setSelectedTenantObj] = useState(null);
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedRepair, setSelectedRepair] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    categoryId: '',
    priority: 'medium',
    status: 'pending',
    assignedTo: '',
    estimatedCost: 0,
    actualCost: 0,
  });
  const [createForm, setCreateForm] = useState({
    title: '',
    description: '',
    categoryId: '',
    priority: 'medium',
    estimatedCost: 0,
    tenantId: '',
    propertyId: '',
    unitId: '',
  });

  useEffect(() => {
    fetchRepairs();
    fetchProperties();
    fetchUsers();
    fetchCategories();
  }, []);

  // Only active categories — retired ones stay on existing repairs but must not be
  // selectable for new ones (the API rejects them on create anyway).
  const fetchCategories = async () => {
    try {
      const response = await axios.get('/repair-categories?isActive=true');
      setCategories(response.data || []);
    } catch (error) {
      toast.error('Error fetching repair categories');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Error fetching users');
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await axios.get('/properties');
      setProperties(response.data);
    } catch (error) {
      toast.error('Error fetching properties');
    }
  };

  const fetchUnits = async (propertyId) => {
    if (!propertyId) {
      setUnits([]);
      return;
    }
    try {
      const property = properties.find(p => p._id === propertyId);
      if (property && property.propertyType !== 'commercial') {
        const response = await axios.get(`/units/property/${propertyId}`);
        setUnits(response.data);
      } else {
        setUnits([]);
      }
    } catch (error) {
      toast.error('Error fetching units');
    }
  };

  const fetchRepairs = async () => {
    try {
      const response = await axios.get('/repairs');
      setRepairs(response.data);
    } catch (error) {
      toast.error('Error fetching repair records');
    }
  };

  const handleEdit = (repair) => {
    setSelectedRepair(repair._id);
    setFormData({
      title: repair.title || '',
      description: repair.description || '',
      categoryId: repair.category?._id || repair.category?.id || repair.categoryId || '',
      priority: repair.priority || 'medium',
      status: repair.status || 'pending',
      // Nested includes are not _id-shimmed (only the top-level record is), so read both.
      // This must prefill correctly: handleSubmit now sends assignedToId explicitly, so a
      // blank value here would clear the assignee on every save of an approved repair.
      assignedTo: repair.assignedTo?._id || repair.assignedTo?.id || '',
      estimatedCost: repair.estimatedCost ?? 0,
      actualCost: repair.actualCost ?? 0,
    });
    setOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (!scopeLocked && !executionLocked) {
        // Not a reachable state (a repair cannot be both draft and approved), but guard
        // rather than silently sending nothing if the statuses ever change.
        toast.error('This repair cannot be edited at its current approval stage');
        return;
      }

      // Only send what the current approval stage actually permits. The server rejects
      // the whole request if it contains a locked field, so sending the full form would
      // 409 on every approved repair.
      const payload = {};

      if (!scopeLocked) {
        payload.title = formData.title;
        payload.description = formData.description;
        // '' is the "no category" option; send null so the FK column is cleared
        // rather than rejected as an invalid UUID.
        payload.categoryId = formData.categoryId || null;
        payload.priority = formData.priority;
        payload.estimatedCost = formData.estimatedCost === '' ? 0 : Number(formData.estimatedCost);
      }

      if (!executionLocked) {
        if (formData.status === 'completed' && (formData.actualCost === '' || formData.actualCost === null || Number(formData.actualCost) < 0)) {
          toast.error('Please enter the actual cost when marking the repair as completed.');
          return;
        }
        payload.status = formData.status;
        payload.assignedToId = formData.assignedTo || null;
        payload.actualCost = formData.actualCost === '' ? 0 : Number(formData.actualCost);
      }

      await axios.put(`/repairs/${selectedRepair}`, payload);
      toast.success('Repair record updated');
      setOpen(false);
      fetchRepairs();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating request');
    }
  };

  const handleCreate = () => {
    setCreateForm({
      title: '',
      description: '',
      categoryId: '',
      priority: 'medium',
      estimatedCost: 0,
      tenantId: '',
      propertyId: '',
      unitId: '',
    });
    setSelectedProperty(null);
    setSelectedTenantObj(null);
    setUnits([]);
    setCreateOpen(true);
  };

  const handleCreateSubmit = async () => {
    try {
      if (!createForm.title || !createForm.description) {
        toast.error('Please fill in title and description');
        return;
      }

      if (!createForm.propertyId && !createForm.unitId) {
        toast.error('Please select a property or unit');
        return;
      }

      await axios.post('/repairs', {
        title: createForm.title,
        description: createForm.description,
        categoryId: createForm.categoryId || null,
        priority: createForm.priority,
        estimatedCost: Number(createForm.estimatedCost) || 0,
        tenantId: createForm.tenantId || null,
        propertyId: createForm.propertyId || null,
        unitId: createForm.unitId || null,
      });

      toast.success('Repair record created');
      setCreateOpen(false);
      fetchRepairs();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error creating repair record');
    }
  };

  const handlePropertyChange = (propertyId) => {
    setCreateForm(prev => ({ ...prev, propertyId, unitId: '', tenantId: '' }));
    setSelectedTenantObj(null);
    setSelectedProperty(properties.find(p => p._id === propertyId) || null);
    fetchUnits(propertyId);
  };

  const handleTenantSelect = (tenant) => {
    setSelectedTenantObj(tenant);
    if (!tenant) {
      setCreateForm(prev => ({ ...prev, tenantId: '', propertyId: '', unitId: '' }));
      setSelectedProperty(null);
      setUnits([]);
      return;
    }
    const propertyId = tenant.property?._id || tenant.property || '';
    const unitId = tenant.unit?._id || tenant.unit || '';
    const property = properties.find(p => p._id === propertyId) || null;
    setCreateForm(prev => ({ ...prev, tenantId: tenant._id, propertyId, unitId }));
    setSelectedProperty(property);
    if (propertyId) fetchUnits(propertyId);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      in_progress: 'info',
      completed: 'success',
      cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  const editingRepair = repairs.find((r) => r._id === selectedRepair) || null;
  const editApprovalStatus = editingRepair?.approvalStatus || 'draft';
  // Mirrors canEditScope/canEditExecution on the server. Scope describes what the work is
  // and is frozen once submitted; execution covers doing the work and unlocks on approval.
  const scopeLocked = editApprovalStatus !== 'draft';
  const executionLocked = editApprovalStatus !== 'approved';

  // The picker only lists active categories, but a repair may have been filed under one
  // that has since been retired. Without adding it back here the Select renders blank and
  // saving would silently strip the category off the record.
  const editCategoryOptions = (() => {
    const current = editingRepair?.category;
    const currentId = current?._id || current?.id;
    if (currentId && !categories.some((c) => c._id === currentId)) {
      return [...categories, { _id: currentId, name: `${current.name} (inactive)` }];
    }
    return categories;
  })();

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'default',
      medium: 'info',
      high: 'warning',
      urgent: 'error',
    };
    return colors[priority] || 'default';
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">
          Repair Record Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          Add Repair Record
        </Button>
      </Box>
      <TableToolbar
        search={query}
        onSearchChange={setQuery}
        searchPlaceholder="Title, description, tenant or property"
        onReset={reset}
        resultCount={resultCount}
        totalCount={totalCount}
        filters={[
            { key: 'status', label: 'Status', value: filterValues.status || '',
              onChange: (v) => setFilter('status', v),
              options: [
                { value: 'pending', label: 'Pending' },
                { value: 'in_progress', label: 'In progress' },
                { value: 'completed', label: 'Completed' },
                { value: 'cancelled', label: 'Cancelled' }
              ] },
            { key: 'approvalStatus', label: 'Approval', value: filterValues.approvalStatus || '',
              onChange: (v) => setFilter('approvalStatus', v), minWidth: 200,
              options: [
                { value: 'draft', label: 'Draft' },
                { value: 'pending_review', label: 'Pending review' },
                { value: 'pending_approval', label: 'Pending final approval' },
                { value: 'approved', label: 'Approved' },
                { value: 'rejected', label: 'Rejected' }
              ] }
        ]}
      />
      <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell className="pin-left">Title</TableCell>
              <TableCell>Approval</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Tenant</TableCell>
              <TableCell>Property/Unit</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Est. Cost</TableCell>
              <TableCell>Actual Cost</TableCell>
              <TableCell>Date</TableCell>
              <TableCell className="pin-right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRepairs.map((repair) => (
              <TableRow key={repair._id}>
                <TableCell className="pin-left">{repair.title}</TableCell>
                <TableCell>
                  <ApprovalChip status={repair.approvalStatus} />
                </TableCell>
                <TableCell>{repair.category?.name || '-'}</TableCell>
                <TableCell>
                  {repair.tenant?.name || 'N/A'}
                  {repair.unit && ` (Unit: ${repair.unit.unitNumber || 'N/A'})`}
                </TableCell>
                <TableCell>
                  {repair.unit ? (
                    <>
                      {repair.unit.property?.propertyName || repair.property?.propertyName} - 
                      Unit {repair.unit.unitNumber}
                    </>
                  ) : (
                    repair.property?.propertyName || 'N/A'
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={repair.priority}
                    color={getPriorityColor(repair.priority)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={repair.status}
                    color={getStatusColor(repair.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {repair.estimatedCost != null && repair.estimatedCost > 0
                    ? formatMoney(repair.estimatedCost, { decimals: 0 })
                    : '–'}
                </TableCell>
                <TableCell>
                  {repair.actualCost != null && repair.actualCost > 0
                    ? formatMoney(repair.actualCost, { decimals: 0 })
                    : '–'}
                </TableCell>
                <TableCell>
                  {format(new Date(repair.createdAt), 'MMM dd, yyyy')}
                </TableCell>
                <TableCell className="pin-right">
                  <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleEdit(repair)}
                    >
                      Update
                    </Button>
                    <RepairApprovalActions repair={repair} onChanged={fetchRepairs} />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {visibleRepairs.length === 0 && (
              <TableRow>
                <TableCell colSpan={11}>
                  <EmptyState title="No repair requests yet" />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </ScrollTable>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            Update Repair Record
            <ApprovalChip status={editApprovalStatus} />
          </Box>
        </DialogTitle>
        <DialogContent>
          {editingRepair?.lastQueryComment && editApprovalStatus === 'draft' && (
            <Alert severity="warning" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
              <strong>Sent back for changes:</strong> {editingRepair.lastQueryComment}
            </Alert>
          )}
          {scopeLocked && executionLocked && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {editApprovalStatus === 'rejected'
                ? 'This repair was rejected and is read-only.'
                : 'This repair is awaiting a decision and cannot be edited until it is approved or sent back.'}
            </Alert>
          )}
          {!scopeLocked && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Draft: you can change what the work is. Assignment, progress and actual cost
              unlock once the repair has been approved.
            </Alert>
          )}
          {!executionLocked && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Approved: record progress and actual cost here. The approved scope
              (title, description, category, priority, estimate) is locked.
            </Alert>
          )}

          <TextField
            fullWidth
            label="Title"
            margin="normal"
            required
            disabled={scopeLocked}
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          />
          <TextField
            fullWidth
            label="Description"
            margin="normal"
            required
            multiline
            rows={3}
            disabled={scopeLocked}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
          <TextField
            fullWidth
            select
            label="Category"
            margin="normal"
            disabled={scopeLocked}
            value={formData.categoryId}
            onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {editCategoryOptions.map((c) => (
              <MenuItem key={c._id} value={c._id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            select
            label="Priority"
            margin="normal"
            disabled={scopeLocked}
            value={formData.priority}
            onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
          >
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="urgent">Urgent</MenuItem>
          </TextField>
          <TextField
            fullWidth
            select
            label="Status"
            margin="normal"
            disabled={executionLocked}
            helperText={executionLocked ? 'Available once the repair is approved' : undefined}
            value={formData.status}
            onChange={(e) =>
              setFormData({ ...formData, status: e.target.value })
            }
          >
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </TextField>
          {formData.status === 'completed' && (
            <TextField
              fullWidth
              label="Actual Cost"
              type="number"
              margin="normal"
              required
              disabled={executionLocked}
              value={formData.actualCost}
              onFocus={(e) => e.target.select()}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  actualCost: e.target.value === '' ? '' : parseFloat(e.target.value) || 0,
                })
              }
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Enter the actual cost when marking as completed."
              error={formData.actualCost !== '' && formData.actualCost !== null && Number(formData.actualCost) < 0}
            />
          )}
          <TextField
            fullWidth
            select
            label="Assigned To"
            margin="normal"
            disabled={executionLocked}
            value={formData.assignedTo}
            onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
          >
            <MenuItem value="">Unassigned</MenuItem>
            {users.map((user) => (
              <MenuItem key={user._id} value={user._id}>
                {user.name} {user.email ? `(${user.email})` : ''}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            label="Estimated Cost"
            type="number"
            margin="normal"
            disabled={scopeLocked}
            value={formData.estimatedCost}
            onFocus={(e) => e.target.select()}
            onChange={(e) =>
              setFormData({
                ...formData,
                estimatedCost: parseFloat(e.target.value) || 0,
              })
            }
            inputProps={{ min: 0, step: 0.01 }}
          />
          {formData.status !== 'completed' && (
            <TextField
              fullWidth
              label="Actual Cost"
              type="number"
              margin="normal"
              disabled={executionLocked}
              value={formData.actualCost}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  actualCost: parseFloat(e.target.value) || 0,
                })
              }
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Enter when status is set to Completed."
            />
          )}

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Approval History
            </Typography>
            {selectedRepair && <RepairApprovalHistory repairId={selectedRepair} />}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              // Nothing is editable while a decision is pending or after rejection.
              (scopeLocked && executionLocked) ||
              (formData.status === 'completed' &&
                (formData.actualCost === '' || formData.actualCost === null || Number(formData.actualCost) < 0))
            }
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Repair Record Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Repair Record</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                required
                value={createForm.title}
                onChange={(e) =>
                  setCreateForm({ ...createForm, title: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                required
                multiline
                rows={4}
                value={createForm.description}
                onChange={(e) =>
                  setCreateForm({ ...createForm, description: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Category"
                value={createForm.categoryId}
                onChange={(e) =>
                  setCreateForm({ ...createForm, categoryId: e.target.value })
                }
                helperText={
                  categories.length === 0
                    ? 'No categories set up yet — an admin can add them under Repair Categories'
                    : 'Optional'
                }
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c._id} value={c._id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Priority"
                value={createForm.priority}
                onChange={(e) =>
                  setCreateForm({ ...createForm, priority: e.target.value })
                }
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Estimated Cost"
                type="number"
                value={createForm.estimatedCost}
                onFocus={(e) => e.target.select()}
                onChange={(e) =>
                  setCreateForm({ ...createForm, estimatedCost: parseFloat(e.target.value) || 0 })
                }
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Expected cost for the repair (e.g. in UGX)"
              />
            </Grid>
            {/* Tenant search */}
            <Grid item xs={12}>
              <TenantPicker
                valueId={selectedTenantObj?._id || ''}
                label="Tenant (optional)"
                onChange={(value) => handleTenantSelect(value)}
              />
            </Grid>

            {/* Auto-filled summary when tenant selected */}
            {selectedTenantObj && (
              <Grid item xs={12}>
                <Box sx={{ p: 1.5, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.light', borderRadius: 1 }}>
                  <Typography variant="body2" color="success.dark">
                    <strong>Tenant selected:</strong> {selectedTenantObj.name}
                    {selectedTenantObj.phone && ` · ${selectedTenantObj.phone}`}
                    {createForm.propertyId && ` · ${properties.find(p => p._id === createForm.propertyId)?.propertyName || ''}`}
                    {createForm.unitId && units.length > 0 && ` · Unit ${units.find(u => u._id === createForm.unitId)?.unitNumber || ''}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Property and unit have been auto-filled. You may change them below if needed.
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Property — always shown; editable independently */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Property"
                value={createForm.propertyId}
                onChange={(e) => handlePropertyChange(e.target.value)}
              >
                <MenuItem value="">Select Property</MenuItem>
                {properties.map((property) => (
                  <MenuItem key={property._id} value={property._id}>
                    {property.propertyName} - {property.addressStreet}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Unit — shown for non-commercial properties */}
            {selectedProperty && selectedProperty.propertyType !== 'commercial' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Unit (Optional)"
                  value={createForm.unitId}
                  onChange={(e) =>
                    setCreateForm(prev => ({ ...prev, unitId: e.target.value }))
                  }
                  SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 260 } } } }}
                >
                  <MenuItem value="">No specific unit</MenuItem>
                  {units.map((unit) => (
                    <MenuItem key={unit._id} value={unit._id}>
                      {unit.unitNumber} - {unit.bedrooms}BR/{unit.bathrooms}BA
                      {unit.assignedTenant && ` (${unit.assignedTenant.name})`}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateSubmit} 
            variant="contained"
            disabled={!createForm.title || !createForm.description || (!createForm.propertyId && !createForm.unitId)}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default RepairManagement;

